// UsersPage.tsx — admin UI listing every subject (humans + k8s service accounts);
// row click drills into detail. Service accounts are stored as
// `<sa>@<ns>.cluster.local` by the authz controller's SA mirror, so the same
// flat list contains both kinds — this page parses the suffix to render a kind
// pill + namespace column and offers a kind filter.
import { useState, useEffect, useCallback, useMemo } from 'react'
import { useNavigate } from 'react-router-dom'
import Card from 'react-bootstrap/Card'
import Form from 'react-bootstrap/Form'
import Alert from 'react-bootstrap/Alert'
import Spinner from 'react-bootstrap/Spinner'
import Table from 'react-bootstrap/Table'
import Badge from 'react-bootstrap/Badge'
import ButtonGroup from 'react-bootstrap/ButtonGroup'
import ToggleButton from 'react-bootstrap/ToggleButton'
import Combobox from 'common/ui/Combobox'
import { useAuth, apiFetch } from '../hooks/useAuth'

type Kind = 'user' | 'sa'

interface ParsedSubject {
    raw: string
    kind: Kind
    sa: string
    namespace: string
}

// Service accounts land in the subjects table as `<sa>@<ns>.cluster.local`
// (see products/authz/server/handlers/sa_tokens.go saSubject()). Anything else
// is treated as a human user — we don't validate email shape here.
const parseSubject = (raw: string): ParsedSubject => {
    const m = raw.match(/^([^@]+)@([^@]+)\.cluster\.local$/)
    if (m) return { raw, kind: 'sa', sa: m[1], namespace: m[2] }
    return { raw, kind: 'user', sa: '', namespace: '' }
}

type KindFilter = 'all' | Kind

const UsersPage = () => {
    const navigate = useNavigate()
    const { email, loading, error: authError } = useAuth()
    const [subjects, setSubjects] = useState<ParsedSubject[]>([])
    const [search, setSearch] = useState('')
    const [kindFilter, setKindFilter] = useState<KindFilter>('all')
    const [fetchErr, setFetchErr] = useState('')

    const fetchUsers = useCallback(async () => {
        setFetchErr('')
        try {
            const res = await apiFetch('/api/admin/users')
            if (!res.ok) {
                setFetchErr('Failed to load users — admin scope required')
                return
            }
            const data = await res.json()
            const list: string[] = data.users || []
            const parsed = list.map(parseSubject)
            parsed.sort((a, b) => a.raw.localeCompare(b.raw))
            setSubjects(parsed)
        } catch {
            setFetchErr('Failed to load users')
        }
    }, [])

    useEffect(() => {
        if (!loading && email) fetchUsers()
    }, [loading, email, fetchUsers])

    const counts = useMemo(() => {
        let users = 0, sas = 0
        for (const s of subjects) {
            if (s.kind === 'sa') sas++; else users++
        }
        return { all: subjects.length, user: users, sa: sas }
    }, [subjects])

    const filteredSubjects = useMemo(() => {
        const q = search.trim().toLowerCase()
        return subjects.filter((s) => {
            if (kindFilter !== 'all' && s.kind !== kindFilter) return false
            if (!q) return true
            return s.raw.toLowerCase().includes(q)
        })
    }, [subjects, search, kindFilter])

    const comboOptions = useMemo(() => subjects.map((s) => s.raw), [subjects])

    const handleSearchSubmit = (e: React.FormEvent) => {
        e.preventDefault()
        const q = search.trim()
        if (!q) return
        const exact = subjects.find((s) => s.raw === q)
        if (exact) navigate(`/users/${encodeURIComponent(exact.raw)}`)
    }

    if (loading) {
        return (
            <div className="d-flex justify-content-center p-5">
                <Spinner animation="border" />
            </div>
        )
    }

    const filterOptions: { value: KindFilter; label: string; count: number }[] = [
        { value: 'all', label: 'All', count: counts.all },
        { value: 'user', label: 'Users', count: counts.user },
        { value: 'sa', label: 'ServiceAccounts', count: counts.sa },
    ]

    return (
        <>
            <h1 className="mb-4">Manage Users</h1>
            {authError && <Alert variant="danger">{authError}</Alert>}
            {fetchErr && <Alert variant="danger">{fetchErr}</Alert>}

            <Card className="mb-3">
                <Card.Header className="d-flex justify-content-between align-items-center">
                    <span>
                        Subjects ({filteredSubjects.length}{(search || kindFilter !== 'all') ? ` of ${subjects.length}` : ''})
                    </span>
                    <ButtonGroup size="sm">
                        {filterOptions.map((opt) => (
                            <ToggleButton
                                key={opt.value}
                                id={`kind-filter-${opt.value}`}
                                type="radio"
                                variant="outline-secondary"
                                name="kind-filter"
                                value={opt.value}
                                checked={kindFilter === opt.value}
                                onChange={(e) => setKindFilter(e.currentTarget.value as KindFilter)}
                            >
                                {opt.label} <Badge bg="secondary">{opt.count}</Badge>
                            </ToggleButton>
                        ))}
                    </ButtonGroup>
                </Card.Header>
                <Card.Body>
                    <Form onSubmit={handleSearchSubmit} className="mb-3">
                        <Form.Group>
                            <Form.Label>Search</Form.Label>
                            <Combobox
                                value={search}
                                onChange={setSearch}
                                options={comboOptions}
                                style={{ width: '100%' }}
                                placeholder="filter by email or <sa>@<ns>.cluster.local — Enter on an exact match to open it"
                            />
                        </Form.Group>
                    </Form>
                    {filteredSubjects.length > 0 ? (
                        <Table size="sm" hover responsive className="m-0">
                            <thead>
                                <tr>
                                    <th>Subject</th>
                                    <th>Kind</th>
                                    <th>Namespace</th>
                                </tr>
                            </thead>
                            <tbody>
                                {filteredSubjects.map((s) => (
                                    <tr
                                        key={s.raw}
                                        style={{ cursor: 'pointer' }}
                                        onClick={() => navigate(`/users/${encodeURIComponent(s.raw)}`)}
                                    >
                                        <td>
                                            <code>{s.kind === 'sa' ? `${s.sa}@${s.namespace}.cluster.local` : s.raw}</code>
                                        </td>
                                        <td>
                                            {s.kind === 'sa'
                                                ? <Badge bg="" className="border border-info text-info">ServiceAccount</Badge>
                                                : <Badge bg="" className="border border-secondary text-secondary">User</Badge>}
                                        </td>
                                        <td>
                                            {s.kind === 'sa' ? <code>{s.namespace}</code> : <span className="text-muted">—</span>}
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </Table>
                    ) : subjects.length === 0 ? (
                        <span className="text-muted small">No subjects found</span>
                    ) : (
                        <span className="text-muted small">No subjects match the current filter</span>
                    )}
                </Card.Body>
            </Card>
        </>
    )
}

export default UsersPage
